<template>
  <form class="space-y-4" @submit="onSubmit">
    <div class="flex justify-between">
      <h2>{{ t('basicInfo') }}</h2>
      <DropdownDotted :buttons="dottedMenuItems" />
    </div>
    <div class="grid grid-cols-1 items-start gap-4 md:grid-cols-3">
      <div class="grid grid-cols-1 gap-2">
        <SelectField name="vehicleTypeId" :label="t('vehicleType')" :options="vehicleTypeOptions" />
        <InputField
          name="licencePlate"
          :label="t('licencePlate')"
          :placeholder="t('licencePlatePlaceholder')"
        />
        <InputField v-if="!initialCar.isGhostCar" name="fin" :label="t('fin')" />
        <LabeledText :label="t('fleetNumber')">
          {{ initialCar.fleetNumber }}
        </LabeledText>
      </div>

      <div class="grid grid-cols-1 gap-4 md:col-span-2 md:grid-cols-6">
        <img
          v-if="imageUrl"
          class="mb-3 self-center md:col-span-4"
          :src="imageUrl"
          :width="600"
          :height="300"
        />
        <div
          v-else
          class="h-50 flex w-full items-center justify-center rounded-md bg-white text-primary md:col-span-4"
        >
          {{ t('noImage') }}
        </div>
        <div class="space-y-2 self-center md:col-span-2">
          <CVButton class="w-full" @click.prevent="showUploadImageModal = true">
            {{ t('changeImage') }}
          </CVButton>
          <CVButton
            v-if="formValues.selectedImageFile"
            variant="danger"
            class="w-full"
            @click.prevent="cancelImageChange"
          >
            {{ t('resetImage') }}
          </CVButton>
          <CVButton
            variant="danger"
            class="w-full"
            :disabled="!carImageUrl"
            @click.prevent="removeImageClick"
          >
            {{ t('removeImage') }}
          </CVButton>
        </div>
      </div>
    </div>

    <div v-if="!initialCar.isGhostCar" class="space-y-4">
      <Divider />

      <div class="flex flex-col items-start gap-2 md:flex-row md:items-center md:justify-between">
        <h2>{{ t('carStatus') }}</h2>

        <CVButton class="w-full md:w-auto" @click.prevent="gotoEditCarStatus">
          {{ t('editCarStatusAndDamages') }}
        </CVButton>
      </div>

      <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
        <SelectField name="tyres" :label="t('tyres')" :options="tyresOptions" />

        <LabeledText :label="t('status')">{{ t(`carStatus.${initialCar?.status}`) }}</LabeledText>

        <LabeledText :label="t('lastTuvDate')">
          <div class="flex items-center gap-2">
            <span>{{ formattedLastTuvDate }}</span>
            <button class="-mt-1 text-2xl text-hazel-600" @click.prevent="showTodosModal = true">
              <PencilIcon class="size-4" />
            </button>
          </div>
        </LabeledText>
      </div>

      <div class="space-y-2">
        <WorkshopAppointmentsSection :car="initialCar" />
      </div>
    </div>

    <div v-if="!initialCar.isGhostCar" class="space-y-4">
      <Divider />

      <h2>{{ t('availability') }}</h2>

      <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
        <DatepickerField
          :disabled="initialCar.status !== CarStatus.ORDERED"
          :label="t('availableFrom')"
          :name="`availableFrom`"
          :tooltip="t('availableFromTooltip')"
        />

        <DatepickerField
          :disabled="initialCar.status === CarStatus.FLOAT_OUT"
          :label="t('availableUntil')"
          :name="`availableUntil`"
        />

        <BooleanSelectField
          name="hiddenFromPublic"
          :label="t('hiddenFromPublic')"
          :tooltip="t('hiddenFromPublicTooltip')"
        />

        <BooleanSelectField name="notAtTheHub" :label="t('externalLocation')" />
      </div>
    </div>

    <Divider />

    <CarStations :stations="stations" :error="errors.stations" />

    <Divider />

    <AdditionalCarStations :stations="stations" />

    <Divider />

    <template v-if="(legalEntities?.length ?? 0) > 1">
      <CarLegalEntities />

      <Divider />
    </template>

    <div v-if="initialCar.isGhostCar">
      <PossibleUpgradeCars
        :station-id="currentStation.id"
        :title="t('possibleUpgradeCars')"
        :errors="upgradeCarErrors"
      />

      <Divider />
    </div>

    <h2>{{ t('specificCarDetails') }}</h2>

    <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
      <SelectField
        v-if="!initialCar.isGhostCar"
        name="acquisitionType"
        :label="t('acquisitionType')"
        :options="acquisitionTypeOptions"
      />

      <InputField
        v-if="
          (formValues.acquisitionType === CarAcquisitionType.PURCHASED_NEW ||
            formValues.acquisitionType === CarAcquisitionType.PURCHASED_USED) &&
          !initialCar.isGhostCar
        "
        name="purchasePrice"
        :label="t('purchasePrice')"
        type="number"
        :max-fraction-digits="2"
        :min="0"
        adornment="€"
        :tooltip="t('purchasePriceInfo')"
      />

      <InputField
        v-if="formValues.acquisitionType === CarAcquisitionType.LEASED && !initialCar.isGhostCar"
        name="leasingRate"
        :label="t('leasingRate')"
        type="number"
        :max-fraction-digits="2"
        :min="0"
        adornment="€"
        :tooltip="t('leasingRateInfo')"
      />

      <InputField
        v-if="!initialCar.isGhostCar"
        name="listPrice"
        :label="t('listPrice')"
        type="number"
        :max-fraction-digits="2"
        :min="0"
        adornment="€"
        :tooltip="t('listPriceInfo')"
      />

      <SelectField name="color" :label="t('color')" :options="colorOptions" empty-value-to="null" />

      <InputField
        v-if="$can('update', 'Car', 'insuranceCompany') && !initialCar.isGhostCar"
        name="insuranceCompany"
        :label="t('insurance')"
        :placeholder="t('insurance')"
      />

      <DatepickerField
        v-if="!initialCar.isGhostCar"
        :label="t('firstRegistration')"
        :name="`firstRegistration`"
      />

      <div
        v-if="!initialCar.isGhostCar"
        class="col-span-full grid grid-cols-1 gap-4 md:grid-cols-3"
      >
        <InputField
          name="salePrice"
          :label="t('salePrice')"
          type="number"
          :max-fraction-digits="2"
          :min="0"
          adornment="€"
          :tooltip="t('salePriceInfo')"
        />

        <DatepickerField :label="t('saleDate')" name="saleDate" />

        <InputLabel
          v-if="showBookValue"
          :label="t('bookValueToday')"
          :tooltip="t('bookValueTodayInfo')"
        >
          <div class="pt-0.5 font-semibold">
            {{ formattedBookValue }}
          </div>
        </InputLabel>
      </div>

      <BooleanSelectField
        v-if="initialCar.isGhostCar"
        name="isMysteryCar"
        :label="t('mysteryCar')"
        :tooltip="t('isMysteryCarInfo')"
      />
    </div>

    <Divider />

    <h2>{{ t('technicalData') }}</h2>

    <TechnicalDataFields :vehicle-type="selectedVehicleType" :fuel-type="formValues.fuelType" />

    <Divider />

    <h2>{{ t('features') }}</h2>

    <TowHitchFields />

    <Divider />

    <h2>{{ t('featuresOverwritesVehicleType') }}</h2>

    <FeaturesFields :vehicle-type="selectedVehicleType" />

    <Divider />

    <h2>{{ t('promotionalVideo') }}</h2>

    <PromotionalVideoField
      name="promotionalVideoMediaId"
      :initial-media="initialCar.promotionalVideo"
      :fallback="selectedVehicleType?.promotionalVideo"
    />

    <div v-if="!initialCar.isGhostCar">
      <Divider />

      <h2>{{ t('documents') }}</h2>

      <CarDocuments v-if="initialCar" :car="initialCar" />
    </div>

    <Divider />

    <div class="flex justify-between">
      <div>
        <CVButton
          v-if="initialCar.isGhostCar && initialCar.status !== CarStatus.FLOAT_OUT"
          :is-loading="isSaving"
          variant="error"
          outline
          @click="fleetOut"
          >{{ t('fleetOut') }}</CVButton
        >
      </div>
      <CVButton variant="default" :is-loading="isSaving" type="submit" :disabled="isSaving">
        {{ t('save') }}
      </CVButton>
    </div>

    <SelectImageForUploadModal
      v-model:show-modal="showUploadImageModal"
      :image-file="formValues.selectedImageFile"
      @select="onSelectImage"
    ></SelectImageForUploadModal>

    <TodosModal v-model="showTodosModal" :car="initialCar" :active-tab="VehiclePoolTab.TUV" />
  </form>
</template>

<script lang="ts" setup>
import BooleanSelectField from '@/components/BooleanSelectField.vue';
import DatepickerField from '@/components/DatepickerField.vue';
import Divider from '@/components/Divider.vue';
import InputField from '@/components/InputField.vue';
import SelectField from '@/components/SelectField.vue';
import PromotionalVideoField from '@/components/media/PromotionalVideoField.vue';
import { CarColor } from '@/entities/cars/car-color.enum';
import type { Car, UpdateCarDto } from '@/entities/cars/car.entity';
import { usePreventLeavingUnsavedForm } from '@/hooks/use-prevent-leaving-unsaved-form';
import { useI18n } from 'vue-i18n';
import router from '@/router';
import { editCarSchema, editGhostCarSchema } from '@/validation/car.schema';
import { useForm } from 'vee-validate';
import { computed, ref, watch } from 'vue';
import CarDocuments from './components/CarDocuments.vue';
import type { Station } from '@/entities/station/station.entity';
import { formHasChanged } from '@/hooks/use-form-has-changed';
import SelectImageForUploadModal from '@/components/SelectImageForUploadModal.vue';
import { cloneDeep } from 'lodash';
import CarStations from './components/CarStations.vue';
import TechnicalDataFields from '@/views/vehicle-pool/add-new-car/TechnicalDataFields.vue';
import type { VehicleType } from '@/entities/vehicle-type.entity';
import FeaturesFields from '@/views/vehicle-pool/add-new-car/FeaturesFields.vue';
import TowHitchFields from '@/views/vehicle-pool/add-new-car/TowHitchFields.vue';
import { CarAcquisitionType } from '@/entities/cars/car-acquisition-type.enum';
import { CarTyres } from '@/entities/cars/car-tyres.enum';
import LabeledText from '@/components/LabeledText.vue';
import InputLabel from '@/components/InputLabel.vue';
import { useFormattedDate } from '@/hooks/use-formatted-date';
import { getDateTimeFormat } from '@/hooks/use-date-format';
import { CarStatus } from '@/entities/cars/car-status.enum';
import DropdownDotted from '@/components/DropdownDotted.vue';
import PossibleUpgradeCars from './components/PossibleUpgradeCars.vue';
import { useCurrentStationOfCar } from '@/hooks/use-current-station-of-car';
import { getInitialValues } from './components/get-initial-values';
import { Alert } from '@/services/alert.service';
import { PencilIcon } from 'lucide-vue-next';
import WorkshopAppointmentsSection from '../components/WorkshopAppointmentsSection.vue';
import TodosModal from '../vehicle-pool-list/components/TodosModal.vue';
import { VehiclePoolTab } from '../vehicle-pool-list/vehicle-pool-tab.enum';
import AdditionalCarStations from './components/AdditionalCarStations.vue';
import CarLegalEntities from './components/CarLegalEntities.vue';
import { useLegalEntities } from '@/queries/use-legal-entities';
import { calculateBookValue } from '@/utils/calculate-book-value';
import { formatCurrency } from '@/utils/format-numbers';

export interface CarFormValues extends UpdateCarDto {
  selectedImageFile?: File | null;
  possibleUpgradeCars?: (
    | (Pick<Car, 'id' | 'licencePlate'> & {
        vehicleType: Omit<VehicleType, 'availableExtras'>;
      })
    | undefined
  )[];
  /** Form-only; merged into `stations` with `primary: false` on submit. */
  additionalStations?: {
    id: string | null;
    fromDate: string | null;
    untilDate: string | null;
  }[];
}

const props = defineProps<{
  initialCar: Car;
  isSaving: boolean;
  vehicleTypes: VehicleType[];
  selectedVehicleType: VehicleType;
  stations: Station[];
}>();
const emit = defineEmits<{
  (e: 'submit', value: CarFormValues): void;
  (e: 'vehicleTypeChange', value: string): void;
}>();

const { t } = useI18n();

const showUploadImageModal = ref(false);
const showTodosModal = ref(false);
const selectedImageUrl = ref(props.initialCar?.imageUrl);

const currentStation = useCurrentStationOfCar(props.initialCar);
// The legal entity assignment is only relevant once there are several legal entities.
const { data: legalEntities } = useLegalEntities();

const formattedLastTuvDate = useFormattedDate(
  computed(() => props.initialCar?.lastTuvDate),
  getDateTimeFormat(),
  '-',
);
const tyresOptions = computed(() => {
  return Object.values(CarTyres).map((tyres) => ({
    label: t(`carTyres.${tyres}`),
    value: tyres,
  }));
});

const acquisitionTypeOptions = computed(() => {
  return Object.values(CarAcquisitionType).map((acquisitionType) => ({
    label: t(`carAcquisitionType.${acquisitionType}`),
    value: acquisitionType,
  }));
});

const showBookValue = computed(
  () =>
    !props.initialCar.isGhostCar &&
    (formValues.acquisitionType === CarAcquisitionType.PURCHASED_NEW ||
      formValues.acquisitionType === CarAcquisitionType.PURCHASED_USED),
);

const formattedBookValue = computed(() => {
  const purchasePrice = formValues.purchasePrice;
  const availableFrom = formValues.availableFrom;
  if (purchasePrice == null || !availableFrom || !Number.isFinite(Number(purchasePrice))) {
    return '-';
  }
  return formatCurrency(calculateBookValue(Number(purchasePrice), availableFrom));
});

const colorOptions = computed(() => {
  return Object.values({
    EMPTY: null,
    ...CarColor,
  }).map((color) => ({
    label: color ? t(`carColors.${color}`) : '',
    value: color,
  }));
});

const vehicleTypeOptions = computed(() => {
  return props.vehicleTypes.map((vehicleType) => ({
    label: `${vehicleType.make.name} ${vehicleType.model}`,
    value: vehicleType.id,
  }));
});

const initialValues = ref<CarFormValues>(getInitialValues(props.initialCar));

const vehicleTypeImageUrl = computed(
  () =>
    props.initialCar?.vehicleType?.imageUrl ??
    props.vehicleTypes.find((type) => type.id === formValues.vehicleTypeId)?.imageUrl,
);
const carImageUrl = ref<string | null | undefined>(props.initialCar?.imageUrl);
const imageUrl = computed(
  () => selectedImageUrl.value || carImageUrl.value || vehicleTypeImageUrl.value,
);

watch(
  () => props.initialCar?.imageUrl,
  () => {
    carImageUrl.value = props.initialCar?.imageUrl;
  },
);

const removeImageClick = () => {
  selectedImageUrl.value = null;
  carImageUrl.value = null;
  setFieldValue('selectedImageFile', null);
};

const gotoEditCarStatus = () => {
  router.push({
    name: 'editCarStatus',
    params: { id: props.initialCar?.id },
  });
};

const {
  handleSubmit,
  values: formValues,
  setFieldValue,
  errors,
} = useForm({
  initialValues: initialValues.value,
  validationSchema: !props.initialCar.isGhostCar
    ? editCarSchema(props.initialCar.status)
    : editGhostCarSchema,
});

const onSelectImage = (newImageFile: File, newImageUrl: string) => {
  setFieldValue('selectedImageFile', newImageFile);
  selectedImageUrl.value = newImageUrl;
};

const cancelImageChange = () => {
  setFieldValue('selectedImageFile', undefined);
  selectedImageUrl.value = props.initialCar?.imageUrl;
};

const upgradeCarErrors = computed(() => {
  const upgradeCarErrors = Object.entries(errors.value).filter(
    ([key, error]) => key.startsWith('possibleUpgradeCars') && error,
  ) as unknown as [string, string[]];
  return upgradeCarErrors.length > 0 ? upgradeCarErrors.map(([, value]) => value) : undefined;
});

watch(
  () => formValues.vehicleTypeId,
  (vehicleTypeId) => {
    if (!vehicleTypeId) return;
    emit('vehicleTypeChange', vehicleTypeId);
  },
);

const fleetOut = async () => {
  const { isConfirmed } = await Alert.fire({
    text: t('reallyFleetOutText'),
    icon: 'warning',
    showDenyButton: true,
    confirmButtonText: t('fleetOut'),
    denyButtonText: t('cancel'),
    focusDeny: true,
  });
  if (isConfirmed) {
    emit('submit', { status: CarStatus.FLOAT_OUT, availableUntil: new Date() });
  }
};

const onSubmit = handleSubmit((values) => {
  emit('submit', values);
  setFieldValue('selectedImageFile', undefined);
  initialValues.value = cloneDeep(values);
});

usePreventLeavingUnsavedForm(() => formHasChanged(initialValues.value, formValues));

const dottedMenuItems = computed(() => [
  {
    title: t('addMaintenanceBlock'),
    onClick: () =>
      router.push({
        name: 'maintenanceEdit',
        params: { carId: props.initialCar.id, blockId: 'new' },
      }),
  },
  {
    title: t('addPartnerRent'),
    onClick: () =>
      router.push({
        name: 'partnerRentEdit',
        params: { carId: props.initialCar.id, bookingId: 'new' },
      }),
  },
]);
</script>

<i18n lang="json">
{
  "en": {
    "basicInfo": "Basic Info",
    "vehicleType": "Vehicle Type",
    "fin": "FIN",
    "fleetNumber": "Fleet no.",
    "changeImage": "Change Image",
    "removeImage": "× Remove Image",
    "carStatus": "Car Status",
    "tyres": "Tyres",
    "status": "Status",
    "lastTuvDate": "Last TÜV Date",
    "editCarStatusAndDamages": "View/Edit Car Status & Damages",
    "availability": "Availability",
    "specificCarDetails": "Specific Car Details",
    "color": "Color",
    "acquisitionType": "Acquisition Type",
    "purchasePrice": "Purchase Price",
    "purchasePriceInfo": "The actual gross list price of the car minus discount (incl. transfer costs)",
    "leasingRate": "Leasing Rate",
    "leasingRateInfo": "The gross leasing rate incl. discount",
    "listPrice": "List Price",
    "listPriceInfo": "The gross list price",
    "salePrice": "Sale Price",
    "salePriceInfo": "The net sale price",
    "saleDate": "Date of Sale",
    "insurance": "Insurance",
    "availableFrom": "Available from",
    "availableUntil": "Available until",
    "technicalData": "Technical Data (overwrites Vehicle Type)",
    "fuelType": "Fuel Type",
    "transmission": "Transmission",
    "seats": "Seats",
    "power": "Power",
    "accelaration": "Acceleration (0 - 100)",
    "drive": "Drive",
    "tankVolume": "Tank Volume",
    "batteryCapacity": "Battery Capacity",
    "range": "Range",
    "features": "Features",
    "featuresOverwritesVehicleType": "Features (overwrites Vehicle Type)",
    "promotionalVideo": "Promotional video",
    "gpsNavigation": "GPS/Navigation",
    "distanceControl": "Distance Control ...",
    "rearViewCamera": "... incl. Rear View Camera",
    "fromVehicleType": "From Vehicle Type",
    "documents": "Documents",
    "noImage": "No Image",
    "fuelLevel": "Fuel Level",
    "milage": "Milage",
    "licencePlatePlaceholder": "i.e. M-CV 1234",
    "availableFromTooltip": "Car is bookable on the website from this date",
    "firstRegistration": "First Registration",
    "bookValueToday": "Book value today",
    "bookValueTodayInfo": "Depreciation is calculated from the actual gross purchase price and the available-from date. (Not first registration, which can be wrong for used-car purchases.)",
    "hiddenFromPublic": "Hide from public website",
    "hiddenFromPublicTooltip": "The car is hidden from the public website but remains visible to brokers via the API.",
    "externalLocation": "External location",
    "resetImage": "Reset image",
    "addMaintenanceBlock": "+ Add Maintenance Block",
    "addPartnerRent": "+ Add Partner Rent",
    "mysteryCar": "Mystery car",
    "isMysteryCarInfo": "The customer will not know which car they will get until they receive the bookings confirmation mail.",
    "possibleUpgradeCars": "Possible Upgrade Cars",
    "fleetOut": "Fleet Out",
    "reallyFleetOutText": "The car will be removed from the fleet. Really fleet out?",
    "workshopAppointments": "Workshop appointments",
    "appointmentRange": "Period",
    "appointmentStatus": "Status",
    "additionalInformation": "Additional information"
  },
  "de": {
    "basicInfo": "Grundlegende Daten",
    "vehicleType": "Vehicle Type",
    "fin": "FIN",
    "fleetNumber": "FP-Nr.",
    "changeImage": "Bild ändern",
    "removeImage": "× Bild löschen",
    "carStatus": "Fahrzeugstatus",
    "tyres": "Bereifung",
    "status": "Status",
    "lastTuvDate": "Letzter TÜV Termin",
    "editCarStatusAndDamages": "Fahrzeugstatus & Schäden ansehen/bearbeiten",
    "availability": "Verfügbarkeit",
    "specificCarDetails": "Spezifische Fahrzeugdaten",
    "color": "Farbe",
    "acquisitionType": "Beschaffungsart",
    "purchasePrice": "Kaufpreis",
    "purchasePriceInfo": "Der tatsächliche Brutto-Listenpreis des Autos abzüglich Rabatt (inkl. Überführungskosten)",
    "leasingRate": "Leasingrate",
    "leasingRateInfo": "Die Brutto Leasingrate inkl. Nachlass",
    "listPrice": "Listenpreis",
    "listPriceInfo": "Der Brutto Listenpreis",
    "salePrice": "Verkaufspreis",
    "salePriceInfo": "Der Verkaufspreis ist als Nettobetrag anzugeben",
    "saleDate": "Datum von Verkauf",
    "insurance": "Versicherung",
    "availableFrom": "Verfügbar ab",
    "availableUntil": "Verfügbar bis",
    "technicalData": "Technische Daten (überschreibt Vehicle Type)",
    "fuelType": "Treibstofftyp",
    "transmission": "Getriebe",
    "seats": "Sitze",
    "power": "Leistung",
    "accelaration": "Beschleunigung (0 - 100)",
    "drive": "Antrieb",
    "tankVolume": "Tankgröße",
    "batteryCapacity": "Batteriekapazität",
    "range": "Reichweite",
    "features": "Ausstattung",
    "featuresOverwritesVehicleType": "Ausstattung (Überschreibt Vehicle Type)",
    "promotionalVideo": "Werbevideo",
    "gpsNavigation": "GPS/Navigation",
    "distanceControl": "Parkabstandswarner ...",
    "rearViewCamera": "... inkl. Rückfahrkamera",
    "fromVehicleType": "Von Vehicle Type",
    "documents": "Dokumente",
    "noImage": "Kein Bild",
    "fuelLevel": "Tankfüllung",
    "milage": "Kilometerstand",
    "licencePlatePlaceholder": "zB.: M-CV 1234",
    "availableFromTooltip": "Auto ist ab diesem Datum auf der Webseite buchbar",
    "firstRegistration": "Erstzulassung",
    "bookValueToday": "Buchwert heute",
    "bookValueTodayInfo": "Die Abschreibung berechnet sich aus dem tatsächlichen Kaufpreis Brutto und dem Verfügbar ab Datum. (Nicht Erstzulassung, da das bei Gebrauchtwagenkäufen falsch sein kann)",
    "hiddenFromPublic": "Auf Webseite ausblenden",
    "hiddenFromPublicTooltip": "Das Fahrzeug wird auf der öffentlichen Webseite ausgeblendet, ist aber für Broker weiterhin über die API sichtbar.",
    "externalLocation": "Auswärtiger Standort",
    "resetImage": "Bild zurücksetzen",
    "addMaintenanceBlock": "+ Wartungsblock hinzufügen",
    "addPartnerRent": "+ Partnermiete hinzufügen",
    "mysteryCar": "Mystery Car",
    "isMysteryCarInfo": "Der Kunde weiß nicht, welches Fahrzeug er bekommt, bis er die Buchungsbestätigungsmail erhält.",
    "possibleUpgradeCars": "Mögliche Upgrade Autos",
    "fleetOut": "Ausflotten",
    "reallyFleetOutText": "Das Fahrzeug wird aus der Flotte entfernt. Wirklich ausflotten?",
    "workshopAppointments": "Werkstatttermine",
    "appointmentRange": "Zeitraum",
    "appointmentStatus": "Status",
    "additionalInformation": "Zusätzliche Informationen"
  },
  "es": {
    "basicInfo": "Datos básicos",
    "vehicleType": "Tipo de vehículo",
    "fin": "Bastidor (VIN)",
    "fleetNumber": "N.º flota",
    "changeImage": "Cambiar imagen",
    "removeImage": "× Quitar imagen",
    "carStatus": "Estado del vehículo",
    "tyres": "Neumáticos",
    "status": "Estado",
    "lastTuvDate": "Última ITV",
    "editCarStatusAndDamages": "Ver/editar estado y daños",
    "availability": "Disponibilidad",
    "specificCarDetails": "Datos específicos del vehículo",
    "color": "Color",
    "acquisitionType": "Tipo de adquisición",
    "purchasePrice": "Precio de compra",
    "purchasePriceInfo": "Precio de lista bruto real del vehículo menos descuento (incl. costes de entrega)",
    "leasingRate": "Cuota de leasing",
    "leasingRateInfo": "Cuota de leasing bruta incl. descuento",
    "listPrice": "Precio de lista",
    "listPriceInfo": "Precio de lista bruto",
    "salePrice": "Precio de venta",
    "salePriceInfo": "El precio de venta debe indicarse como importe neto",
    "saleDate": "Fecha de venta",
    "insurance": "Seguro",
    "availableFrom": "Disponible desde",
    "availableUntil": "Disponible hasta",
    "technicalData": "Datos técnicos (sobrescribe el tipo de vehículo)",
    "fuelType": "Combustible",
    "transmission": "Transmisión",
    "seats": "Plazas",
    "power": "Potencia",
    "accelaration": "Aceleración (0–100)",
    "drive": "Tracción",
    "tankVolume": "Capacidad del depósito",
    "batteryCapacity": "Capacidad de batería",
    "range": "Autonomía",
    "features": "Equipamiento",
    "featuresOverwritesVehicleType": "Equipamiento (sobrescribe el tipo de vehículo)",
    "promotionalVideo": "Vídeo promocional",
    "gpsNavigation": "GPS/Navegación",
    "distanceControl": "Control de distancia…",
    "rearViewCamera": "… incl. cámara trasera",
    "fromVehicleType": "Del tipo de vehículo",
    "documents": "Documentos",
    "noImage": "Sin imagen",
    "fuelLevel": "Nivel de combustible/carga",
    "milage": "Kilometraje",
    "licencePlatePlaceholder": "p. ej. M-1234-AB",
    "availableFromTooltip": "El vehículo es reservable en la web a partir de esta fecha",
    "firstRegistration": "Primera matriculación",
    "bookValueToday": "Valor contable hoy",
    "bookValueTodayInfo": "La amortización se calcula a partir del precio de compra bruto real y la fecha de disponible desde. (No la primera matriculación, que puede ser incorrecta en compras de vehículos de ocasión.)",
    "hiddenFromPublic": "Ocultar en la web pública",
    "hiddenFromPublicTooltip": "El vehículo queda oculto en la web pública, pero sigue siendo visible para brokers a través de la API.",
    "externalLocation": "Ubicación externa",
    "resetImage": "Restablecer imagen",
    "addMaintenanceBlock": "+ Añadir bloque de mantenimiento",
    "addPartnerRent": "+ Añadir alquiler partner",
    "mysteryCar": "Vehículo sorpresa",
    "isMysteryCarInfo": "El cliente no sabrá qué vehículo recibirá hasta el correo de confirmación de la reserva.",
    "possibleUpgradeCars": "Posibles vehículos de upgrade",
    "fleetOut": "Baja de flota",
    "reallyFleetOutText": "El vehículo se eliminará de la flota. ¿Confirmas la baja?",
    "workshopAppointments": "Citas de taller",
    "appointmentRange": "Periodo",
    "appointmentStatus": "Estado",
    "additionalInformation": "Información adicional"
  }
}
</i18n>
